import { useRef, useState, useEffect } from 'react';
import { formatTime, getOffset } from './helper';
import { Time, CircleBackground, CircleProgress, Container, Content, StyledSvg } from './view';

interface ITimer {
    time: number; // в секундах
    onTimeOut: () => void;
    size?: number;
    progressBarWidth?: number;
}

export const Timer = (props: ITimer) => {
    const { time, onTimeOut, size = 81, progressBarWidth = 5 } = props;

    const [timeLeft, setTimeLeft] = useState<number>(time);
    const [progress, setProgress] = useState<number>(100);

    // Реф для основного таймера на UI
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    // Реф для таймера закрытия модалки
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Параметры для SVG
    const backgroundRadius = size / 2;
    const progressRadius = size / 2 - progressBarWidth / 2;
    const progressCircumference = 2 * Math.PI * progressRadius;

    const clearTimerRef = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    const clearTimeoutRef = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    useEffect(() => {
        const updateProgress = () => {
            // 'timeLeft - 1' - отнимаем 1, чтобы анимация запускалась до того, как пользователь увидит обратный отсчёт.
            // 'Math.max(0, ...)' - чтобы не было отрицательного значения.
            const updatedProgress = Math.max(0, ((timeLeft - 1) / time) * 100);
            setProgress(updatedProgress);
        };
        //timeLeft === time - фактически говорит о том, что progress = 100%.
        // Не используем progress, чтобы не триггерить вызов useEffect лишние разы.
        if (timeLeft === time) {
            // Таймаут нужен, чтобы пользователь успел увидеть значение 100 на таймере.
            setTimeout(updateProgress, 500);
        } else {
            updateProgress();
        }
    }, [timeLeft, time]);

    useEffect(() => {
        if (timeLeft === 0) {
            clearTimerRef();
            // Использую Timeout, чтобы пользователь успел увидеть значение 0 на таймере.
            timeoutRef.current = setTimeout(() => {
                onTimeOut();
            }, 300);
            return;
        }

        if (timerRef.current) return;

        timerRef.current = setInterval(() => {
            setTimeLeft((prev) => prev - 1);
        }, 1000);
    }, [timeLeft, onTimeOut]);

    useEffect(() => {
        return () => {
            clearTimerRef();
            clearTimeoutRef();
        };
    }, []);

    return (
        <Container $size={size}>
            <StyledSvg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                {/* Фоновое круг */}
                <CircleBackground
                    cx={size / 2}
                    cy={size / 2}
                    r={backgroundRadius}
                    $progressBarWidth={progressBarWidth}
                />
                {/* Линия прогресса */}
                <CircleProgress
                    cx={size / 2}
                    cy={size / 2}
                    r={progressRadius}
                    strokeDasharray={`${getOffset(progress, progressCircumference)} ${progressCircumference}`}
                    $progressBarWidth={progressBarWidth}
                />
            </StyledSvg>
            <Content>
                <Time variant="title8">{formatTime(timeLeft)}</Time>
            </Content>
        </Container>
    );
};
